import Image from 'next/image'

import { PromotionsEnquireDrawer } from '@/components/common/PromotionsEnquireDrawer/PromotionsEnquireDrawer'
import { Button, ButtonLink } from '@/components/primitives/Button/Button'
import { Heading } from '@/components/primitives/Heading/Heading'
import { cn, getImageUrl } from '@/lib/utils'

import RichTextEditor from '../RichTextEditor/RichTextEditor'

import type { SlideType } from '@/types/contentful/heroBannerType'

export const Banner = ({
  data,
  className,
  bannerSize,
  priority = false,
}: {
  data: SlideType['fields']
  className?: string
  bannerSize?: '708' | '516'
  // Only the first/active slide should claim high fetch priority. Marking every
  // slide priority dilutes the hint so the browser drops it on the real LCP image.
  priority?: boolean
}) => {
  const { image, actionButton, subtitle, title, enableShapeOverlay, enquireFormLink } = data
  const shapeOverlay = !!enableShapeOverlay

  const largeBanner = bannerSize === '708'
  const smallBanner = bannerSize === '516'
  const heightSlider = largeBanner ? 'lg:min-h-banner-large desktop:min-h-banner-large-desktop' : 'lg:min-h-banner-small desktop:min-h-banner-small-desktop'
  const mobileHeight = shapeOverlay ? heightSlider : `min-h-[320px] ${heightSlider}`
  const mobileMask = largeBanner ? '[mask-image:url(/images/slider-mask-mobile.svg)]' : '[mask-image:url(/images/banner-mask-mobile.svg)]'
  const desktopMask = largeBanner ? 'lg:[mask-image:url(/images/slider-mask-desktop.svg)]' : 'lg:[mask-image:url(/images/banner-mask-desktop.svg)]'

  const renderCtaButton = () => {
    if (actionButton?.fields?.label && enquireFormLink) {
      return (
        <PromotionsEnquireDrawer formSrc={enquireFormLink}>
          <Button variant="solid" className="w-fit bg-accent-green-3">
            {actionButton.fields.label}
          </Button>
        </PromotionsEnquireDrawer>
      )
    }

    if (actionButton?.fields?.url) {
      return (
        <ButtonLink variant="solid" className="w-fit bg-accent-green-3" href={actionButton.fields.url}>
          {actionButton.fields.label}
        </ButtonLink>
      )
    }

    return null
  }

  const ctaButton = renderCtaButton()

  return (
    <div className={cn('relative flex min-h-full w-full flex-col items-center overflow-hidden lg:flex-row lg:justify-center', mobileHeight, className)}>
      {image && (
        <Image
          src={getImageUrl(image)}
          alt="Hero banner"
          width={shapeOverlay ? 1100 : 584}
          height={shapeOverlay ? 1100 : 584}
          className={cn(`right-0 lg:absolute lg:top-1/2 lg:-translate-y-1/2`, {
            [`${mobileMask} ${desktopMask}`]: shapeOverlay,
            'aspect-[1/1] h-auto min-h-[276px] object-cover [mask-position:bottom] [mask-size:cover] sm:min-h-[330px] lg:h-full lg:min-h-0 lg:w-[50%] lg:[mask-position:unset]':
              shapeOverlay,
            'max-w-[700px] inset-x-auto w-[80vw] sm:w-[70vw] lg:right-12 desktop:right-30 lg:w-[40vw]': !shapeOverlay,
            'max-w-[600px]': smallBanner && !shapeOverlay,
            'mt-[95px] md:mt-0': largeBanner && !shapeOverlay,
          })}
          sizes="(max-width: 1024px) 100vw, 50vw"
          priority={priority}
          loading={priority ? 'eager' : 'lazy'}
        />
      )}

      <div
        className={cn('flex w-full flex-col justify-center gap-8 px-6 py-7 lg:px-12 xl:px-[120px] desktop:mx-auto desktop:max-w-content desktop:pl-0', {
          'justify-start lg:justify-center': shapeOverlay,
          'lg:justify-center': smallBanner && !shapeOverlay,
        })}
      >
        <div className="flex w-full flex-col gap-4 md:max-w-[480px]">
          <div className="flex flex-col gap-4">
            {title && (
              <Heading as="h1" colour="white" className={cn(bannerSize !== '708' && 'text-5xl leading-[48px] xl:text-5xl desktop:text-[64px] desktop:leading-[63px]')}>
                {title}
              </Heading>
            )}
            {subtitle && (
              <div className={cn('text-base font-medium text-white', bannerSize === '708' ? 'xl:text-lg' : 'xl:text-base desktop:text-lg')}>
                {<RichTextEditor content={subtitle} />}
              </div>
            )}
          </div>
          {ctaButton && <div className="h-13 w-fit rounded-full bg-border-gradient p-[1.2px]">{ctaButton}</div>}
        </div>
      </div>
    </div>
  )
}
